import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Shopping still works in-memory when storage is unavailable.
  }
};

const makeLineKey = (productId, option = "") => `${productId}::${option}`;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage(siteConfig.storage.cartKey, [])
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage(siteConfig.storage.wishlistKey, [])
  );
  const [notice, setNotice] = useState("");

  useEffect(() => writeStorage(siteConfig.storage.cartKey, cartItems), [cartItems]);
  useEffect(
    () => writeStorage(siteConfig.storage.wishlistKey, wishlist),
    [wishlist]
  );

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 2400);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const addCart = useCallback((productId, quantity = 1, option = "") => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    const lineKey = makeLineKey(productId, option);
    setCartItems((items) => {
      const existing = items.find((item) => item.lineKey === lineKey);
      if (existing) {
        return items.map((item) =>
          item.lineKey === lineKey
            ? { ...item, quantity: item.quantity + safeQuantity }
            : item
        );
      }
      return [...items, { id: productId, option, quantity: safeQuantity, lineKey }];
    });
    setNotice("Added to your chilled bag");
  }, []);

  const removeCart = useCallback((productId, option = "") => {
    const lineKey = makeLineKey(productId, option);
    setCartItems((items) => items.filter((item) => item.lineKey !== lineKey));
  }, []);

  const updateQuantity = useCallback((productId, option = "", quantity) => {
    const lineKey = makeLineKey(productId, option);
    const nextQuantity = Number(quantity);
    setCartItems((items) =>
      nextQuantity < 1
        ? items.filter((item) => item.lineKey !== lineKey)
        : items.map((item) =>
            item.lineKey === lineKey
              ? { ...item, quantity: nextQuantity }
              : item
          )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId) ? items : [...items, productId]
    );
    setNotice("Saved to favourites");
  }, []);

  const removeWishlist = useCallback((productId) => {
    setWishlist((items) => items.filter((id) => id !== productId));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((items) => {
      const isSaved = items.includes(productId);
      setNotice(isSaved ? "Removed from favourites" : "Saved to favourites");
      return isSaved
        ? items.filter((id) => id !== productId)
        : [...items, productId];
    });
  }, []);

  const itemCount = useMemo(
    () => cartItems.reduce((count, item) => count + item.quantity, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      wishlist,
      itemCount,
      notice,
      addCart,
      addToCart: addCart,
      removeCart,
      removeFromCart: removeCart,
      updateQuantity,
      clearCart,
      addWishlist,
      removeWishlist,
      toggleWishlist,
    }),
    [
      addCart,
      addWishlist,
      cartItems,
      clearCart,
      itemCount,
      notice,
      removeCart,
      removeWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      <div
        className={`site-toast ${notice ? "site-toast--visible" : ""}`}
        role="status"
        aria-live="polite"
      >
        {notice}
      </div>
    </CartContext.Provider>
  );
};
